/* eslint-disable eqeqeq */
/* eslint-disable no-underscore-dangle */
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, PLATFORM_ID } from '@angular/core';
import { Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { InputStatus } from '../../../../directive/input-mask/input-mask-processing.base';
import { maskInput } from '../../../../directive/input-mask/mask.helper';
import { getValidationObject } from '../../../../helper/validation-converter/validation-converter';
import { CoreBuildingBlockControlBaseComponent } from '../../shared/building-block-control-base.component';
import { CoreBuildingBlock } from '../../shared/building-block.decorator';
import { matchDomain } from 'src/utils';

@CoreBuildingBlock('input')
@Component({
  selector: 'cu-input',
  templateUrl: './input.component.html',
  standalone: false
})
export class InputComponent extends CoreBuildingBlockControlBaseComponent implements OnDestroy, OnInit {
  /** element reference input  */
  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;
  private subscription = Subscription.EMPTY;

  /** input status */
  private lastState: InputStatus;
  /** masked value */
  private _maskedValue: string;
  /** input type string */
  public inputType = 'text';
  /** input autocomplete boolean */
  public inputAutocomplete = 'off';
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** value change callback */
  public readonly onValueChange = (): void => {
    const fieldValue = this.field?.data?.value?.toString();
    // When Save Application Data to Session Storage Feature Flag is turned ON
    // Removing the logic to patch empty values onLoad for input fields
    if (fieldValue?.trim().length > 0) {
      if (this.isBrowser) {
        this.input.nativeElement.value = fieldValue;
      }
      this.processMask();
    }
  };

  public ngOnDestroy(): void {
    if (this.isAutoAddress && this.subscription) {
      this.addressService.unsubscribe(this.subscription);
    }

    if (this.field?.focused) {
      this.field.focused = false;
      this.field.data.saveUnlock();
    }

    this.field.data.unsubscribe(this.onValueChange);
  }

  get isAutoAddress() {
    if (this.isBrowser) {
      const hostname = window.location.hostname;
      if (!matchDomain(/^[\w-]+\.myori\.com$|^myori\.com$/, hostname)) {
        // Google Places Autocomplete is disabled for non-myori.com domains
        return false;
      } else {
        return this.field?.config?.autoAddress;
      }
    }
    return false;
  }

  public ngOnInit(): void {
    if (this.isAutoAddress) {
      if (this.isBrowser) {
        this.subscription = this.addressService.autocomplete(this.field, this.input.nativeElement);
      }
    }

    setTimeout(this.onValueChange, 0);
    this.field.data.onChange(this.onValueChange);
    if (!this.autoCompleteEnabled) {
      this.inputType = 'search';
      this.inputAutocomplete = 'something-new';
    }
    super.ngOnInit();
  }

  /**
   * Getter / setter for masked value. Both get & set trigger mask processing
   */
  public get maskedValue(): string {
    return this._maskedValue;
  }

  public set maskedValue(str: string) {
    this._maskedValue = str;
    this.processMask();
  }

  public processMask(setSelection = true): void {
    this.addPrefixtoDate();
    this.removePrefixZero();
    const mask = maskInput(this.field.config.mask, this.readState(), this.lastState, getValidationObject(this.field));
    if (mask.changed) {
      if (this.isBrowser) {
        this.input.nativeElement.value = mask.processed.value;
        if (setSelection && this.input.nativeElement.value) {
          this.input.nativeElement.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
        }
      }
      this.field.data.value = mask.processed.value; // TODO: change to reverse mask
    }
    this.lastState = this.readState();
    this._maskedValue = mask.processed.value;
    if (this.isBrowser && this.input.nativeElement.getAttribute('type') === 'search' && this.input.nativeElement.hasAttribute('autocomplete')) {
      this.input.nativeElement.removeAttribute('autocomplete');
      this.input.nativeElement.setAttribute('autocomplete', 'something-new');
    }
  }

  private readState(): InputStatus {
    const domElement = this.input.nativeElement;
    return {
      focused: this.focused,
      selection: this.isBrowser ? [domElement.selectionStart, domElement.selectionEnd] : [0, 0],
      value: this.isBrowser ? this.input.nativeElement.value : ''
    };
  }

  /**
   * Blocks special chars and allow alphabets and numbers only
   * @param e
   */
  public blockSpecialChar(e) {
    if (this.field.config.label === 'ZIP/Postal code') {
      const valid = (e.which >= 48 && e.which <= 57) || (e.which >= 65 && e.which <= 90) || (e.which >= 97 && e.which <= 122);
      if (!valid) {
        e.preventDefault();
      }
    }
  }

  /**  TODO: revisit / refactor */
  /*
   * Adds prefix(0) when the input field having value && focus is false
   */
  private addPrefixtoDate(): void {
    // Bug 60725: ECP MTG | For date fields when entered one digit month 'Zero' is not adding automatically during masking
    const state: InputStatus = this.readState();
    if (this.field.config.mask == 'date' && !state.focused && state.value) {
      if (state.value.length == 6) {
        if (this.isBrowser) {
          this.input.nativeElement.value = this.addStr(state.value, 0, '0');
        }
      } else if (state.value.length == 8) {
        if (this.isBrowser) {
          this.input.nativeElement.value = this.addStr(this.addStr(state.value, 0, '0'), 2, '0');
        }
      } else if (state.value.length == 9) {
        if (this.isBrowser) {
          this.input.nativeElement.value =
            parseInt(state.value.slice(0, 2)) <= 12 ? this.addStr(state.value, 2, '0') : this.addStr(state.value, 0, '0');
        }
      }
    }
  }

  private addStr(str: string, index: number, stringToAdd: string): string {
    return str.substring(0, index) + stringToAdd + str.substring(index, str.length);
  }

  /** TODO: Need to be refactored */
  /*
   * Remove prefix(0) when the input field having value
   */
  private removePrefixZero(): void {
    // Bug 128954: OAO 2.0 | Business membership percent ownership allows 0 as first numeric character
    const state: InputStatus = this.readState();
    if (this.field.config.mask == 'percent' && !state.focused && state.value && state.value.charAt(0) === '0') {
      if (this.isBrowser) {
        this.input.nativeElement.value = parseFloat(state.value) == 0 ? `%` : parseFloat(state.value) + `%`;
      }
    }
  }

  public doFocus() {
    this.focused = true;
    this.processMask();
    if (this.field.config.inputMode === 'numeric' && this.field.config.clearZero && this.field.data.valueNumber === 0) {
      this.field.data.value = '';
    }
  }

  public doBlur() {
    this.focused = false;
    this.processMask(false);
    if (
      this.field.config.inputMode === 'numeric' &&
      this.field.config.clearZero &&
      (this.field.data.value === null || this.field.data.value === '')
    ) {
      this.field.data.value = '0';
    }
  }

  public handleKeydown(event: KeyboardEvent) {
    if (event?.key === 'Enter') {
      if (this.isBrowser) {
        this.input.nativeElement.blur();
      }
    }
  }
}
